import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import { getTermByName } from '../character/content/glossary';
import './TermLink.css';

interface TermLinkProps {
  children: string;
}

export const TermLink = React.memo(({ children }: TermLinkProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const term = getTermByName(children);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (isMobile) setShowTooltip(false);
  }, [isMobile]);

  useEffect(() => {
    if (showTooltip && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setTooltipPos({ top: rect.top - 8, left: rect.left });
    }
  }, [showTooltip]);

  if (!term) return <span>{children}</span>;

  const colorClass = term.color ? `term-link__text--${term.color}` : '';

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(term.path);
    setShowTooltip(false);
  };

  let borderColor = 'var(--gold)';
  if (term.color === 'hope') borderColor = 'var(--hope, #d4af37)';
  else if (term.color === 'fear') borderColor = 'var(--fear, #9d4edd)';

  return (
    <span
      ref={ref}
      className="term-link"
      onMouseEnter={() => !isMobile && setShowTooltip(true)}
      onMouseLeave={() => !isMobile && setShowTooltip(false)}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          navigate(term.path);
          setShowTooltip(false);
        }
      }}
      aria-label={`${children}: ${term.definition}`}
    >
      <span className={`term-link__text ${colorClass}`}>{children}</span>
      {!isMobile && showTooltip && ReactDOM.createPortal(
        <div
          className="term-tooltip"
          style={{
            '--term-top-color': borderColor,
            top: `${tooltipPos.top}px`,
            left: `${tooltipPos.left}px`,
          } as React.CSSProperties}
        >
          <div className="term-tooltip__name">{term.name}</div>
          <div className="term-tooltip__definition">{term.definition}</div>
          <div className="term-tooltip__cta">Go to {term.name}</div>
        </div>,
        document.body
      )}
    </span>
  );
});

TermLink.displayName = 'TermLink';
